// https://www.smashingmagazine.com/2015/07/designing-simple-pie-charts-with-css/

import { offlinePattern, onlinePattern, impactedPattern, atRiskPattern } from './assets/patterns';

type TextVisibility = 'full' | 'partial' | 'none';
const pieChart = (values, textVisibility: TextVisibility) => {
	const radius = 200;
	const borderWidth = radius * 0.2; // Adjusted for better visibility
	const dashGapLength = 2 * Math.PI * radius;
	const filteredValues = Object.keys(values).reduce(
		(acc, curr) => (values[curr].value > 0 ? { ...acc, [curr]: values[curr] } : acc),
		{},
	);

	const keys = Object.keys(filteredValues);
	const segmentCount = keys.length;
	const totalAssets = Object.keys(filteredValues).reduce(
		(acc, curr) => filteredValues[curr].value + acc,
		0,
	);
	const calcVisual = (v: number) => (v / Math.max(1, totalAssets)) * dashGapLength;

	const separatorPx = segmentCount > 1 ? 18 : 0;
	const singleValueChart = segmentCount === 1;
	const textColor = singleValueChart ? filteredValues[keys[0]].textColor : '#2A2A27';

	let runningTotal = 0;
	const calculatedValues = Object.keys(filteredValues).reduce((acc, curr) => {
		const fullLen = calcVisual(filteredValues[curr].value);
		const visibleLen = Math.max(0, fullLen - separatorPx);
		const res = {
			...acc,
			[curr]: {
				visualPercentage: visibleLen,
				rotation: -runningTotal,
				fillPattenName: filteredValues[curr].fillPattenName,
			},
		};
		runningTotal += fullLen;
		return res;
	}, {});

	const baseRing =
		segmentCount > 1
			? `<circle r="${radius}" cx="${radius}" cy="${radius}"
           style="fill: transparent; stroke: white; stroke-width: ${radius * 2};" />`
			: '';

	const baseColorFor = (k: string) =>
		filteredValues[k]?.baseColor ||
		(filteredValues[k]?.fillPattenName === 'atRiskPattern' ? '#FFBA5A' : '#999');

	const pieChartSegments = keys.map((key) => {
		const { visualPercentage, rotation, fillPattenName } = calculatedValues[key];
		const baseColor = baseColorFor(key);

		return `
            <g
            stroke-dasharray="${visualPercentage} ${dashGapLength}"
            stroke-dashoffset="${rotation}"
            stroke-linecap="butt"
            >
            <circle
                r="${radius}"
                cx="${radius}"
                cy="${radius}"
                fill="transparent"
                stroke="${baseColor}"
                stroke-width="${radius * 2}"
            />
            <!-- stripe overlay ring (slightly narrower to avoid AA seams) -->
            <circle
                r="${radius}"
                cx="${radius}"
                cy="${radius}"
                fill="transparent"
                stroke="url(#${fillPattenName})"
                stroke-width="${radius * 2 - 0.5}"
            />
            </g>
        `;
	});

	const centerCircle = `
        <circle
            r="${singleValueChart ? radius * 0.5 : radius * 0.6}"
            cx="${radius}"
            cy="${radius}"
            style="
                fill: ${singleValueChart ? filteredValues[Object.keys(filteredValues)[0]].secondaryColor : 'white'};
            "
        />
    `;

	const borderCircle = `
        <circle
            r="${radius}"
            cx="${radius}"
            cy="${radius}"
            style="
                fill: transparent; 
                stroke: white; 
                stroke-width: ${borderWidth};
            "
        />
    `;

	const displayTextPartial = `
        <text
            x="${radius}" 
            y="${-radius * 0.95}" 
            font-size="${radius * 0.7}" 
            font-weight="bold" 
            fill="${textColor}"
            text-anchor="middle" 
            dominant-baseline="middle" 
            style="transform: rotate(90deg);"
        >
            ${totalAssets}
        </text>
    `;

	const displayTextFull = `
        <text
            x="${radius}"
            y="${-radius * 1.1}"
            font-size="${radius * 0.5}"
            fill="${textColor}"
            text-anchor="middle"
            dominant-baseline="middle"
            style="transform: rotate(90deg); font-weight: 600"
        >
            ${totalAssets}
        </text>
        <text
            x="${radius}"
            y="${-radius * 0.75}"
            font-size="${radius * 0.3}"
            fill="${textColor}"
            text-anchor="middle"
            dominant-baseline="middle"
            style="transform: rotate(90deg);"
        >
            assets
        </text>
    `;

	const displayText =
		textVisibility !== 'none' &&
		(textVisibility === 'partial' ? displayTextPartial : displayTextFull);

	return `
        <svg
            width="${radius * 2}" 
            height="${radius * 2}" 
            xmlns="http://www.w3.org/2000/svg"
            style="
                transform: rotate(-90deg);
                border-radius: 50%;
            "
        >
            <style>
                @font-face {
                    font-family: "Roboto";
                    src: url("http://fonts.googleapis.com/css?family=Roboto:400,100,100italic,300,300italic,400italic,500,500italic,700,700italic,900italic,900")
                }
                text {
                    font-family: "Roboto", sans-serif;
                }
            </style>
            <defs>
                ${offlinePattern}
                ${onlinePattern}
                ${impactedPattern}
                ${atRiskPattern}
            </defs>

            ${baseRing}
            ${pieChartSegments.join(' ')}
            ${centerCircle}
            ${displayText}
            ${borderCircle}
        </svg>
    `;
};

export const generateSvg = ({
	size,
	data,
}: {
	size: 'extraSmall' | 'small' | 'medium' | 'large';
	data: any;
}) => {
	const vis = size === 'medium' || size === 'large' ? 'full' : 'partial';
	return pieChart(data, vis);
};

export const svgToDataUrl = (svg: string): string => {
	const base64 = btoa(svg);
	return `data:image/svg+xml;base64,${base64}`;
};

const AssetCluster = ({ data }) => {
	const svg = generateSvg({
		size: 'small',
		data,
	});
	return <img src={`data:image/svg+xml;utf8,${encodeURIComponent(svg)}`} />;
};

export default AssetCluster;
